// Phase 17 Plan 02 Task 3 — PowerIcon primitive.
//
// Inline-SVG renderer for the 8 power-up icons. Replaces the Unicode
// glyphs the current TopBar uses (POLISH-01); the W3 MatchTopBar
// rebuild will consume PowerIcon directly.
//
// These are stroke-based line icons (`stroke="currentColor"`,
// `strokeWidth={2}`, fill="none"), NOT fill-only paths — see the
// per-name path note below for the take-two RUNA icon-library source.
//
// ARIA: role="img" always set; aria-label defaults to the icon name
// but can be overridden via `title` (e.g., "extra letter" for the
// Quill icon in a localized context).

import type { JSX } from 'react'

export type PowerIconName =
  | 'quill'
  | 'tongue'
  | 'mulligan'
  | 'vow'
  | 'keep'
  | 'sigil'
  | 'vanish'
  | 'whisper'

export interface PowerIconProps {
  name: PowerIconName
  /** SVG width/height in px. Default 24. */
  size?: number
  /** Accessible label override. Defaults to `name`. */
  title?: string
}

/**
 * Per-name path bodies — folk hand-drawn 24×24 glyphs matching the
 * RUNA prototype icon library (Docs/UI-prototype take two/project/src/
 * icons.jsx — ICON ability-glyph set + line icons). Round caps/joins,
 * stroke=currentColor, ~strokeWidth 2 come from the shared <svg> wrapper;
 * filled accents declare `fill="currentColor" stroke="none"` inline.
 *
 * Icon mapping (power-up → closest prototype folk glyph):
 *   quill    → folk quill/pencil (ICON.pencil)
 *   tongue   → ICON.ab_tangle    (entangling knot — "tongue-tie")
 *   mulligan → ICON.ab_gust      (cyclone arrows — re-shuffle)
 *   vow      → ICON.swords       (knight's crossed blades)
 *   keep     → ICON.ab_ward      (warded shield — fortify)
 *   sigil    → ICON.ab_meddle    (rune wand + sparkles — cast)
 *   vanish   → ICON.ab_hop       (dashed leap + fading mote)
 *   whisper  → folk cupped hand cradling a glowing rune (hint)
 */
const PATHS: Record<PowerIconName, JSX.Element> = {
  // Scrivener's Quill — folk quill pen (ICON.pencil), ink mote at the nib.
  quill: (
    <>
      <path d="M4 20l1-4L16 5l3 3L8 19z" />
      <path d="M14 7l3 3" />
      <path d="M5 16l3 3" />
    </>
  ),
  // Tongue-Tie — ICON.ab_tangle: two interlaced bramble loops (a bound knot).
  tongue: (
    <>
      <path d="M7 9a3 3 0 013-3c2 0 3 2 4 4s2 4 4 4a3 3 0 000-6" />
      <path d="M17 15a3 3 0 01-3 3c-2 0-3-2-4-4S8 9 6 9a3 3 0 000 6" />
    </>
  ),
  // Lexical Mulligan — ICON.ab_gust: paired cyclone arrows (re-shuffle).
  mulligan: (
    <>
      <path d="M19.5 9a7.5 7.5 0 00-13-2.5M4.5 15a7.5 7.5 0 0013 2.5" />
      <path d="M19.5 4v5h-5M4.5 20v-5h5" />
    </>
  ),
  // Knight's Vow — ICON.swords: crossed folk blades.
  vow: (
    <>
      <path d="M14.5 4H20v5.5L9 20.5 3.5 15z" />
      <path d="M9.5 4H4v5.5L15 20.5 20.5 15" />
      <path d="M11 13l2 2" />
    </>
  ),
  // Castle Keep — ICON.ab_ward: warded shield with a folk star.
  keep: (
    <>
      <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
      <path d="M12 8l1.2 2.4 2.6.3-1.9 1.8.5 2.6L12 16.2l-2.4 1.2.5-2.6-1.9-1.8 2.6-.3z" />
    </>
  ),
  // Anagram Sigil — ICON.ab_meddle: rune wand striking sparkles.
  sigil: (
    <>
      <path d="M5 19L15 9" />
      <path d="M16 4c.4 2 1 2.6 3 3-2 .4-2.6 1-3 3-.4-2-1-2.6-3-3 2-.4 2.6-1 3-3z" />
      <path d="M7 5l.6 1.8L9.4 7.4 7.6 8 7 9.8 6.4 8 4.6 7.4 6.4 6.8z" />
    </>
  ),
  // The Vanishing — ICON.ab_hop: dashed leap arc + a fading mote.
  vanish: (
    <>
      <path d="M5 17c2-7 12-7 14 0" strokeDasharray="1.5 3" />
      <path d="M16 17h3v-3" />
      <circle cx="5" cy="17" r="1.6" fill="currentColor" stroke="none" />
    </>
  ),
  // Whisperer's Promise — folk cupped hand cradling a glowing rune (hint).
  whisper: (
    <>
      <path d="M5 12.5c0 4 3 7 7 7s7-3 7-7" />
      <path d="M5 12.5c0-1.4.9-2.4 2-2.5M19 12.5c0-1.4-.9-2.4-2-2.5" />
      <path
        d="M12 4.5c.25 2.1.9 2.75 3 3-2.1.25-2.75.9-3 3-.25-2.1-.9-2.75-3-3 2.1-.25 2.75-.9 3-3z"
        fill="currentColor"
        stroke="none"
      />
      <path d="M12 2.6v.9M9.2 4.1l.5.7M14.8 4.1l-.5.7" />
    </>
  ),
}

export function PowerIcon({
  name,
  size = 24,
  title,
}: PowerIconProps): JSX.Element {
  return (
    <svg
      role="img"
      aria-label={title ?? name}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      {PATHS[name]}
    </svg>
  )
}
